import { computePosition, flip, offset, shift } from '@floating-ui/dom';
import { ReactNode, useRef, useState } from 'react';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import { useTypograf } from '@/shared/hooks/use-typograf';

interface Props {
    message: string;
    children?: ReactNode;
}

function Tooltip({ message, children }: Props) {
    const device = useMediaQueryDeviceStateValue();
    const isTablet = device.includes('tablet');
    const isDesktop = device === 'desktop';

    const tpMessage = useTypograf(message) || message;
    const [firstCalculation, setFirstCalculation] = useState(true);
    const floatingRef = useRef<HTMLDivElement>(null);
    const [coords, setCoords] = useState<{ x: number | undefined; y: number | undefined }>({
        x: undefined,
        y: undefined,
    });
    const onPinClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
        if (isTablet) {
            return { x: undefined, y: undefined };
        }

        if (event.target instanceof Element) {
            const reference = event.target.closest('.js-tooltip__wrapper');

            const compute = () =>
                computePosition(reference!, floatingRef.current!, {
                    placement: 'bottom',
                    middleware: [offset(10), shift({ padding: 0 }), flip()],
                });

            if (reference && floatingRef.current) {
                compute()
                    .then((pos) => {
                        if (firstCalculation) {
                            setFirstCalculation(false);
                            return compute();
                        } else {
                            return pos;
                        }
                    })
                    .then((pos) => {
                        setCoords(pos);
                    });
            }
        }
    };

    return (
        <div className="tooltip__wrapper js-tooltip__wrapper" ref={floatingRef}>
            {children && (
                <div className="tooltip__content">
                    {children}
                    <div
                        className="tooltip__info-icon"
                        onClick={(event) => {
                            if (!isDesktop) {
                                event.preventDefault();
                                onPinClick(event);
                            }
                        }}
                        aria-label={tpMessage}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                            <path d="M 24 4 C 12.972066 4 4 12.972074 4 24 C 4 35.027926 12.972066 44 24 44 C 35.027934 44 44 35.027926 44 24 C 44 12.972074 35.027934 4 24 4 z M 24 7 C 33.406615 7 41 14.593391 41 24 C 41 33.406609 33.406615 41 24 41 C 14.593385 41 7 33.406609 7 24 C 7 14.593391 14.593385 7 24 7 z M 24 14 A 2 2 0 0 0 24 18 A 2 2 0 0 0 24 14 z M 23.976562 20.978516 A 1.50015 1.50015 0 0 0 22.5 22.5 L 22.5 33.5 A 1.50015 1.50015 0 1 0 25.5 33.5 L 25.5 22.5 A 1.50015 1.50015 0 0 0 23.976562 20.978516 z"></path>
                        </svg>
                    </div>
                </div>
            )}
            <div className="tooltip__info-wrapper">
                <div className="tooltip__text text-xs" style={{ left: coords.x, top: coords.y }}>
                    {tpMessage}
                </div>
            </div>
        </div>
    );
}

export default Tooltip;
